/**
 * ResearchRunView — the one view of a finished (or finishing) research run,
 * mounted by both the search detail page and the chat card's expanded sheet
 * (DEEP_RESEARCH_REDESIGN.md: one implementation, two mounts).
 *
 * Tabs: Report (markdown + sources, with *Uložiť report*, PRD-0076) · Findings
 * (cards grid). Legacy runs
 * without a report fall back to the plain summary text; legacy verdict
 * data is intentionally not rendered (decision 4 — hidden, not migrated).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReportBody } from '@/components/research/ReportBody'
import { FindingsList } from '@/components/search/detail/FindingsList'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SaveResearchReportAction } from '@/features/hypotheses/SaveResearchReportAction'
import type { HypothesisRun } from '@/types/hypothesis'
import type { HypothesisFinding } from '@/types/hypothesis-finding'

export type ResearchRunTab = 'report' | 'findings'

export interface ResearchRunViewProps {
  caseId: string
  run: HypothesisRun
  findings: HypothesisFinding[] | undefined
  initialTab?: ResearchRunTab
  /** Override for evidence entity clicks — hosts already inside a modal
   * (chat research sheet) drill in instead of stacking another sheet. */
  onOpenEntity?: (nodeId: string) => void
}

export function ResearchRunView({
  caseId,
  run,
  findings,
  initialTab = 'findings',
  onOpenEntity,
}: ResearchRunViewProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<ResearchRunTab>(initialTab)

  const findingCount = findings?.length ?? 0

  return (
    <Tabs
      value={tab}
      onValueChange={v => setTab(v as ResearchRunTab)}
      className="flex flex-col gap-4"
    >
      <TabsList variant="line">
        <TabsTrigger value="findings">
          {t('researchRun.tabs.findings', { count: findingCount })}
        </TabsTrigger>
        <TabsTrigger value="report">{t('researchRun.tabs.report')}</TabsTrigger>
      </TabsList>

      <TabsContent value="report" className="flex flex-col gap-3">
        <div className="flex justify-end">
          <SaveResearchReportAction caseId={caseId} run={run} />
        </div>
        <ReportBody
          run={run}
          findingCount={findingCount}
          onOpenFinding={() => setTab('findings')}
          onOpenEntity={onOpenEntity}
        />
      </TabsContent>

      <TabsContent value="findings">
        <FindingsList findings={findings} latestRun={run} caseId={caseId} />
      </TabsContent>
    </Tabs>
  )
}
